import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Prompt, CreatePromptFormData, UpdatePromptFormData, AiUpdateOptionsFormData, AvailableModel } from './types';
import { getAiOptionsWithPrompts } from 'graphql/questions-and-answers/queries';
import {
  createPrompt as gqlCreatePrompt,
  updatePrompt as gqlUpdatePrompt,
  deletePrompt as gqlDeletePrompt,
  updateAiOptions as gqlUpdateAiOptions,
  AiOptions as AiOptionsMutations,
} from 'graphql/questions-and-answers/mutations';
import { getModelList } from 'services/inference/getModelList';
import { useLLMProxyServiceContext } from 'contexts/llmProxySerivceContext';

const AiConfigContext = createContext<AiConfigContextType | null>(null);

interface AiConfigContextType {
  prompts: Prompt[];
  aiOptions: AiOptionsMutations | null;
  availableModels: AvailableModel[] | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  createPrompt: (data: CreatePromptFormData) => Promise<void>;
  updatePrompt: (data: UpdatePromptFormData) => Promise<void>;
  deletePrompt: (promptId: string) => Promise<void>;
  updateAiOptions: (data: AiUpdateOptionsFormData) => Promise<void>;
  refreshAiOptions: () => Promise<void>;
}

const AiConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { refreshPrompts } = useLLMProxyServiceContext();
  const [aiOptions, setAiOptions] = useState<AiOptionsMutations | null>(null);
  const [availableModels, setAvailableModels] = useState<AvailableModel[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [aiOptionsResponse, modelsResponse] = await Promise.all([
          getAiOptionsWithPrompts(),
          getModelList().catch(() => ({ data: [] })),
        ]);

        setAiOptions(aiOptionsResponse.getAiOptions);
        setAvailableModels((modelsResponse?.data || []) as AvailableModel[]);
      } catch (err) {
        setIsError(true);
        setError(err instanceof Error ? err : new Error('Failed to load AI options'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const createPrompt = useCallback(async (data: CreatePromptFormData) => {
    try {
      const response = await gqlCreatePrompt(data);
      setAiOptions(response.createPrompt);
      await refreshPrompts();
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err : new Error('Failed to create prompt'));
      throw err;
    }
  }, [refreshPrompts]);

  const updatePrompt = useCallback(async (data: UpdatePromptFormData) => {
    try {
      const { promptId, ...promptData } = data;
      const response = await gqlUpdatePrompt({
        prompt: promptData,
        promptId,
      });
      setAiOptions(response.updatePrompt);
      await refreshPrompts();
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err : new Error('Failed to update prompt'));
      throw err;
    }
  }, [refreshPrompts]);

  const deletePrompt = useCallback(async (promptId: string) => {
    try {
      const response = await gqlDeletePrompt(promptId);
      setAiOptions(response.deletePrompt);
      await refreshPrompts();
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err : new Error('Failed to delete prompt'));
      throw err;
    }
  }, [refreshPrompts]);

  const updateAiOptions = useCallback(async (data: AiUpdateOptionsFormData) => {
    try {
      const response = await gqlUpdateAiOptions({
        model_agent: data.model_agent,
        model_vision: data.model_vision,
        prompt_system: data.prompt_system,
        prompt_screen: data.prompt_screen,
        prompt_image: data.prompt_image,
      });
      setAiOptions(response.updateAiOptions);
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err : new Error('Failed to update AI options'));
      throw err;
    }
  }, []);

  const refreshAiOptions = useCallback(async () => {
    try {
      const result = await getAiOptionsWithPrompts();
      setAiOptions(result.getAiOptions);
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err : new Error('Failed to refresh AI options'));
      throw err;
    }
  }, []);

  const sortedPrompts = [...(aiOptions?.prompts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <AiConfigContext.Provider value={{
      prompts: sortedPrompts,
      aiOptions,
      availableModels,
      isLoading,
      isError,
      error,
      createPrompt,
      updatePrompt,
      deletePrompt,
      updateAiOptions,
      refreshAiOptions,
    }}
    >
      {children}
    </AiConfigContext.Provider>
  );
};

export const useAiConfigContext = () => {
  const context = useContext(AiConfigContext);
  if (!context) {
    throw new Error('useAiConfigContext must be used within AiConfigProvider');
  }
  return context;
};

export { AiConfigProvider };
